import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Image,
  Popconfirm,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import adminApiService from '../../services/adminApi';

interface Material {
  id: string;
  title: string;
  title_en: string;
  description: string;
  description_en: string;
  image: string;
  category: string;
  specifications: Record<string, string>;
  features: string[];
  applications: string[];
}

const MaterialsPage: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [form] = Form.useForm();
  const [uploadedImagePath, setUploadedImagePath] = useState<string>('');

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const response = await adminApiService.getData('materials');
      setMaterials(response.data.materials || []);
    } catch (error) {
      message.error('加载材料数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMaterial(null);
    form.resetFields();
    setUploadedImagePath('');
    setModalVisible(true);
  };

  const handleEdit = (material: Material) => {
    setEditingMaterial(material);
    form.setFieldsValue({
      ...material,
      features: material.features?.join('\n') || '',
      applications: material.applications?.join('\n') || '',
      specifications: JSON.stringify(material.specifications || {}, null, 2),
    });
    setUploadedImagePath(material.image);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const newMaterials = materials.filter((m) => m.id !== id);
      await adminApiService.updateData('materials', { materials: newMaterials });
      message.success('删除成功');
      loadMaterials();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const newMaterial: Material = {
        id: values.id || String(Date.now()),
        title: values.title,
        title_en: values.title_en,
        description: values.description,
        description_en: values.description_en,
        image: uploadedImagePath || values.image,
        category: values.category,
        specifications: values.specifications
          ? JSON.parse(values.specifications)
          : {},
        features: values.features
          ? values.features.split('\n').filter((f: string) => f.trim())
          : [],
        applications: values.applications
          ? values.applications.split('\n').filter((a: string) => a.trim())
          : [],
      };

      let newMaterials: Material[];
      if (editingMaterial) {
        // 编辑模式：替换材料
        newMaterials = materials.map((m) =>
          m.id === editingMaterial.id ? newMaterial : m
        );
      } else {
        // 新增模式：添加材料
        newMaterials = [...materials, newMaterial];
      }

      await adminApiService.updateData('materials', { materials: newMaterials });
      message.success(editingMaterial ? '更新成功' : '添加成功');
      setModalVisible(false);
      loadMaterials();
    } catch (error: any) {
      message.error(error.message || '保存失败');
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const response = await adminApiService.uploadFile(file, 'images', 'materials');
      setUploadedImagePath(response.path);
      message.success('图片上传成功');
      return false;
    } catch (error) {
      message.error('图片上传失败');
      return false;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '图片',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (image: string) => (
        <Image src={image} alt="material" className="image-preview" />
      ),
    },
    {
      title: '材料名称',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '英文名称',
      dataIndex: 'title_en',
      key: 'title_en',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => <Tag color="purple">{category}</Tag>,
    },
    {
      title: '特点数量',
      dataIndex: 'features',
      key: 'features',
      width: 100,
      render: (features: string[]) => features?.length || 0,
    },
    {
      title: '应用数量',
      dataIndex: 'applications',
      key: 'applications',
      width: 100,
      render: (applications: string[]) => applications?.length || 0,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Material) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <h2 className="page-header">材料管理</h2>
      <div className="action-buttons">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加材料
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={materials.sort((a, b) => {
          // 尝试将ID转换为数字进行排序，如果不是数字则按字符串排序
          const aNum = Number(a.id);
          const bNum = Number(b.id);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
          return a.id.localeCompare(b.id);
        })}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingMaterial ? '编辑材料' : '添加材料'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={700}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="材料ID（用于排序）"
            name="id"
            rules={[{ required: true, message: '请输入材料ID' }]}
            tooltip="ID用于控制材料显示顺序，数字ID会按数值排序"
          >
            <Input placeholder="例如: 1, 2, 3..." />
          </Form.Item>
          <Form.Item
            label="材料名称（中文）"
            name="title"
            rules={[{ required: true, message: '请输入材料名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="材料名称（英文）"
            name="title_en"
            rules={[{ required: true, message: '请输入英文名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="描述（中文）"
            name="description"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            label="描述（英文）"
            name="description_en"
            rules={[{ required: true, message: '请输入英文描述' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label="图片路径" name="image">
            <Input
              placeholder="图片路径或上传图片"
              value={uploadedImagePath}
              onChange={(e) => setUploadedImagePath(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="上传图片">
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>点击上传</Button>
            </Upload>
            {uploadedImagePath && (
              <div style={{ marginTop: 8 }}>
                <Image src={uploadedImagePath} alt="preview" width={200} />
              </div>
            )}
          </Form.Item>
          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请输入分类' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="规格参数（JSON 格式）" name="specifications">
            <Input.TextArea
              rows={6}
              placeholder='{"参数名": "参数值", ...}'
            />
          </Form.Item>
          <Form.Item label="特点（每行一个）" name="features">
            <Input.TextArea rows={4} placeholder="每行输入一个特点" />
          </Form.Item>
          <Form.Item label="应用场景（每行一个）" name="applications">
            <Input.TextArea rows={4} placeholder="每行输入一个应用场景" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MaterialsPage;
